import {
  WIDGET_SUPPORT_CHATBOT,
  WidgetEvents,
  WidgetMessageData,
} from '@wix/wix-chatbot-common/browser';
import { Store } from 'redux';

import { TRootState } from '../common/reducers';
import {
  IMetaUpdateDealerOfferParams,
  IMetaUpdateOrigParams,
  IMetaUpdatePageUrl,
} from '../common/reducers/types';
import { ACTIONS } from '../constants';
import { emitter } from '../util/emitter';
import { assertUnreachable } from '../util/guards';
import { logger } from '../util/logger';

/**
 * Communication with parent page when the Chatbot is the widget in iframe
 */

const trustedSourcesRegexp = /^https:\/\/(.+)\.(wix|editorx)\.com$/i;

export class WidgetChannel {
  constructor(private readonly _store: Store<TRootState>) {
    window.addEventListener('message', this.onMessage);
  }

  unsubscribe() {
    window.removeEventListener('message', this.onMessage);
  }

  private onMessage = ({
    origin,
    data,
    source,
  }: MessageEvent<WidgetMessageData>): void => {
    const isTrustedSources = trustedSourcesRegexp.test(origin);
    const isWidgetType = data && data.type === WIDGET_SUPPORT_CHATBOT;

    if (
      (!isTrustedSources &&
        // to simulate messages from parent in tests
        source !== window) ||
      !isWidgetType
    ) {
      return;
    }

    const { event, payload } = data;
    switch (event) {
      case WidgetEvents.LOG_MESSAGE:
        logger.logWidgetMessage({
          widgetEventType: data.widgetEventType,
          payload,
        });
        break;
      case WidgetEvents.UPDATE_LOAD_PAGE_URL:
        this._store.dispatch<IMetaUpdatePageUrl>({
          type: ACTIONS.META_UPDATE_PAGE_URL,
          loadPageUrl: payload.loadPageUrl,
          startUrl: payload.startUrl,
        });
        break;
      case WidgetEvents.UPDATE_DEALER_OFFER_PARAMS:
        this._store.dispatch<IMetaUpdateDealerOfferParams>({
          type: ACTIONS.META_UPDATE_DEALER_OFFER_PARAMS,
          dealerOfferParams: payload.dealerOfferParams,
        });
        break;
      case WidgetEvents.UPDATE_ORIG_PARAMS:
        this._store.dispatch<IMetaUpdateOrigParams>({
          type: ACTIONS.META_UPDATE_ORIG_PARAMS,
          origPageName: payload.origPageName,
          origPlatform: payload.origPlatform,
        });
        break;
      case WidgetEvents.TOGGLE_CHATBOT_VISIBLE:
        emitter.emit('toggleChatbotVisible', payload.visible);
        break;
      case WidgetEvents.UPDATE_ARTICLE_URI:
        emitter.emit('updateArticleUri', payload.articleUri);
        break;
      case WidgetEvents.TOGGlE_WIDGET_EXPANDED:
        emitter.emit('widgetExpanded', payload.expanded);
        break;
      case WidgetEvents.NEW:
      case WidgetEvents.GET_ORIG_PARAMS_RESPONSE:
      case WidgetEvents.SET_INITIAL_PARAMS:
      case WidgetEvents.CONTEXT_CHANGE:
      case WidgetEvents.CALL_SDK_METHOD_REQUEST:
      case WidgetEvents.CALL_SDK_METHOD_RESPONSE:
      case WidgetEvents.CALL_INTERNAL_HANDLER_REQUEST:
      case WidgetEvents.CALL_INTERNAL_HANDLER_RESPONSE:
      case WidgetEvents.UPDATE_WIDGET_URL:
        // do not handle this message here
        break;
      default:
        assertUnreachable(event);
    }
  };
}
